import React, { useState } from "react";
import DatePickerField from "components/lib/Shared/DatePickerField";
import { Form, Formik } from "formik";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { useToast } from "hooks/useToast";
import { useDispatch, useSelector } from "react-redux";
import AddSharesOnOffer from "components/CompanyDashboard/MaintainYourCompany/Changes/ShareCapital/AllotmentOfShares/AllotmentForms/addSharesOnOfferForm";
import {
  updateShareAuthorizationAsync,
  getShareAuthorizationsAsync,
} from "state/slices/shareCapitalAuthorization";
import { Constants } from "config/constants";

const ProposedSubrow = (props) => {
  const { toast } = useToast(5000);
  const dispatch = useDispatch();

  const companyOfficialsSlice =
    useSelector((state) => state.companyOfficialsSlice) || {};
  const officials = companyOfficialsSlice.companyOfficials?.items || [];

  const filteredOfficials = officials.filter((official) =>
    [
      Constants.APPLICANT_CAPACITIES.SHAREHOLDER,
      Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER,
      Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER,
    ].includes(official.designation)
  );

  const handleDateIssued = (date) => {};
  const handleDateExpiry = (date) => {};

  // Check if this is an authorization row within privateWithMoreThanOneClass
  const isAuthorizationInPWMOC =
    props.changesMode === "privateWithMoreThanOneClass" &&
    props.row?.original?.transactionChangeType === "AUTHORIZATION";

  // Authorization detection complete

  // Check if this is a transaction-based change type that should show AddSharesOnOffer
  const isTransactionBasedChangeType =
    (props.changesMode === "privateWithMoreThanOneClass" ||
      props.changesMode === "PVTwithOneClass" ||
      props.changesMode === "shareTransfer" ||
      props.changesMode === "forfeitureOfShares" ||
      props.changesMode === "unallotedShares") &&
    !isAuthorizationInPWMOC;

  // Extract transaction data from the row to prefill the form
  const getInitialDataFromTransaction = () => {
    if (!props.row?.original) return {};

    const rowData = props.row.original;

    // Handle different data structures that might exist in the row
    const extractValue = (field) => {
      return (
        rowData[field] ||
        rowData.shareCapitalChange?.[field] ||
        rowData.allotment?.[field] ||
        ""
      );
    };

    // Helper function to safely parse date values
    const parseDate = (dateValue) => {
      if (!dateValue) return null;

      // If it's already a Date object, return it
      if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue;
      }

      // If it's a string, try to parse it
      if (typeof dateValue === "string") {
        const parsed = new Date(dateValue);
        return isNaN(parsed.getTime()) ? null : parsed;
      }

      // If it's a number (timestamp), convert it
      if (typeof dateValue === "number") {
        const parsed = new Date(dateValue);
        return isNaN(parsed.getTime()) ? null : parsed;
      }

      return null;
    };

    const data = {
      classOfShares:
        extractValue("classOfShares") || extractValue("sharesCategory"),
      sharesOnOffer:
        extractValue("sharesOnOffer") || extractValue("sharesNumber"),
      offerPricePerShare:
        extractValue("offerPricePerShare") || extractValue("sharesValue"),
      premiumPerShare: extractValue("premiumPerShare"),
      dateOfOffer: parseDate(extractValue("dateOfOffer")),
      sharesAvailable: extractValue("sharesAvailable"),
    };

    // For authorization mode, ensure only the number of shares is editable and other fields are locked via form component props
    return data;
  };

  // Function to save allotment data to Redux
  const handleUpdateTransaction = async (updatedData) => {
    try {
      if (props.onUpdateTransaction) {
        await props.onUpdateTransaction(props.row.original, updatedData);
        // Close subrow and show success toast for all modes
        props.closeSubRow();
        toast("success", "Transaction updated successfully");
      }
    } catch (error) {
      // Always throw the error so the calling function can handle it
      throw error;
    }
  };

  return (
    <tr className="bg-white w-full p-4">
      <td colSpan={props.visibleColumns.length}>
        {isTransactionBasedChangeType ? (
          <div className="w-full py-3">
            <div className="px-4">
              <AddSharesOnOffer
                onSubmit={handleUpdateTransaction}
                onCancel={props.closeSubRow}
                editMode={true}
                initialData={getInitialDataFromTransaction()}
                allChangeTypes={[props.changesMode]}
                companyId={props.companyId}
                isUpdateMode={true}
                isAuthorizationMode={isAuthorizationInPWMOC}
                filteredOfficials={filteredOfficials}
              />
            </div>
          </div>
        ) : isAuthorizationInPWMOC ? (
          <Formik
            initialValues={{
              sharesOnOffer:
                getInitialDataFromTransaction()?.sharesOnOffer || "",
              classOfShares:
                getInitialDataFromTransaction()?.classOfShares || "",
              offerPricePerShare:
                getInitialDataFromTransaction()?.offerPricePerShare || "",
              premiumPerShare:
                getInitialDataFromTransaction()?.premiumPerShare || "",
              dateOfOffer: getInitialDataFromTransaction()?.dateOfOffer || null,
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setSubmitting(true);

                // Validate the input
                const sharesCount = Number(values.sharesOnOffer);
                if (
                  !sharesCount ||
                  sharesCount < 1 ||
                  !Number.isInteger(sharesCount)
                ) {
                  toast(
                    "error",
                    "Please enter a valid number of shares (must be a positive integer)"
                  );
                  return;
                }

                // Get the authorization data from the row
                const authorizationData = props.row.original;
                // Prepare the update payload with only the changed shares number
                const updatePayload = {
                  ...authorizationData,
                  sharesOnOffer: Number(values.sharesOnOffer),
                  sharesNumber: Number(values.sharesOnOffer), // Also update sharesNumber if needed
                  // Calculate updated nominal value based on new share count
                  nominalValue:
                    Number(values.sharesOnOffer) *
                    Number(authorizationData.offerPricePerShare || 0),
                  // Calculate updated voting rights (shares * votes per share)
                  votingRights:
                    Number(values.sharesOnOffer) *
                    Number(authorizationData.votesPerShare || 0),
                };

                // Dispatch the update action
                const updateResult = await dispatch(
                  updateShareAuthorizationAsync(updatePayload)
                );

                if (
                  updateShareAuthorizationAsync.fulfilled.match(updateResult)
                ) {
                  toast("success", "Authorization updated successfully");

                  // Refresh the authorizations to get the latest data
                  await dispatch(getShareAuthorizationsAsync(props.companyId));

                  // Close the subrow
                  props.closeSubRow();
                } else {
                  throw new Error(
                    updateResult.payload || "Failed to update authorization"
                  );
                }
              } catch (error) {
                console.error("Authorization update error:", error);
                toast(
                  "error",
                  error.message || "Failed to update authorization"
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              isSubmitting,
              handleSubmit,
            }) => (
              <div className="w-full py-3">
                <div className="mb-4 px-4">
                  <h1 className="font-bold text-sm text-gray-900">
                    Edit Authorization Details
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    Only the number of shares can be updated. Other fields are
                    read-only.
                  </p>
                </div>
                <Form className="w-full px-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Class of Shares
                      </label>
                      <input
                        type="text"
                        value={values.classOfShares}
                        readOnly
                        className="mt-1 block w-full bg-gray-100 border border-gray-300 rounded-md shadow-sm p-2 text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of Shares
                      </label>
                      <input
                        type="number"
                        name="sharesOnOffer"
                        value={values.sharesOnOffer}
                        onChange={(e) =>
                          setFieldValue("sharesOnOffer", e.target.value)
                        }
                        min="1"
                        step="1"
                        required
                        className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm p-2 text-gray-700"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Offer Price Per Share
                      </label>
                      <input
                        type="number"
                        value={values.offerPricePerShare}
                        readOnly
                        className="mt-1 block w-full bg-gray-100 border border-gray-300 rounded-md shadow-sm p-2 text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Premium Per Share
                      </label>
                      <input
                        type="number"
                        value={values.premiumPerShare}
                        readOnly
                        className="mt-1 block w-full bg-gray-100 border border-gray-300 rounded-md shadow-sm p-2 text-gray-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Offer
                    </label>
                    <input
                      type="text"
                      value={
                        values.dateOfOffer
                          ? new Date(values.dateOfOffer).toLocaleDateString()
                          : ""
                      }
                      readOnly
                      className="mt-1 block w-full bg-gray-100 border border-gray-300 rounded-md shadow-sm p-2 text-gray-500"
                    />
                  </div>

                  <div className="flex justify-end mt-6 space-x-2">
                    <button
                      type="button"
                      onClick={() => props.closeSubRow()}
                      disabled={isSubmitting}
                      className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                      className="inline-flex justify-center px-4 py-2 ml-auto text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-sflPrimary hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <SpinnerIcon />
                          Updating...
                        </>
                      ) : (
                        "Update Authorization"
                      )}
                    </button>
                  </div>
                </Form>
              </div>
            )}
          </Formik>
        ) : (
          <Formik
            initialValues={{
              dateIssued: "",
              dateOfExpiry: "",
            }}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <div className="w-full py-3">
                <h1 className="pt-2 px-4 font-bold text-sm text-gray-900">
                  Date of Authorization
                </h1>
                <Form className="w-full flex flex-col gap-1 px-4">
                  <div className="flex justify-between gap-6 p-2 px-4">
                    <div className="w-full">
                      <DatePickerField
                        label="Date Issued"
                        onChange={handleDateIssued}
                        name="dateIssued"
                        placeholderText="Select date of issue"
                        errorPath=""
                        disabled={false}
                      />
                    </div>

                    <div className="w-full">
                      <DatePickerField
                        label="Date of Expiry"
                        onChange={handleDateExpiry}
                        name="dateOfExpiry"
                        placeholderText="Select date of expiry"
                        errorPath=""
                        disabled={false}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-2 space-x-2">
                    <button
                      type="button"
                      onClick={() => props.closeSubRow()}
                      disabled={isSubmitting}
                      className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex justify-center px-4 py-2 ml-auto text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-sflPrimary hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
                      style={{ alignSelf: "center" }}
                    >
                      {isSubmitting ? (
                        <>
                          <SpinnerIcon />
                          Submitting...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </Form>
              </div>
            )}
          </Formik>
        )}
      </td>
    </tr>
  );
};

export default ProposedSubrow;
